import { requiredPorts } from "./requiredPorts";
import type { FlowDocument, FlowNode } from "./types";

/**
 * A structural "test call" walkthrough — not a real execution proof. It
 * walks `document`'s own edges by port, the same graph
 * requiredPorts.ts/validateFlow.ts already read, and never resolves what a
 * node's config actually MEANS (no TTS rendering, no HTTP calls, no AMD
 * classification, no DTMF collection): a `gather`/`branch`/`repeat`/
 * `amd_gate`/`transfer` node just asks a human tester which of its own real
 * outcomes to simulate, the same way a real caller's actual input or a real
 * AMD result would decide it. This proves reachability and port wiring —
 * "does every path this graph can take actually go somewhere sensible" —
 * not call semantics.
 *
 * `play`/`tts`/`consent`/`http`/`start`/`tag` auto-advance via their own
 * `"next"` port without asking, even though the first four also have a
 * required `"error"` port: the product owner's own choice (over pausing at
 * every multi-port node) was that walking a flow's happy path should not
 * mean clicking "next" through every single play/tts/consent/http node just
 * to reach the interesting branching further on. `hangup` always ends the
 * call. `transfer`'s only real port is `"failure"` — a successful transfer
 * is a terminal handoff with no exit port of its own — so this offers a
 * synthetic `"success"` choice alongside real `"failure"` whenever
 * `"failure"` is wired, and auto-ends as a success when it is not (nothing
 * else that node could do).
 */

const AUTO_ADVANCE_TYPES = new Set(["start", "play", "tts", "consent", "tag", "http"]);
const AUTO_ADVANCE_PORT = "next";

/** A synthetic outcome, never a real edge port — see the module doc above. */
export const TRANSFER_SUCCESS = "success";

const MAX_STEPS = 500;

export interface SimulationTraceEntry {
  nodeId: string;
  nodeType: string;
  /** How this node was reached; `null` only for the very first (start) entry. */
  arrivedVia: string | null;
}

export interface SimulationState {
  trace: SimulationTraceEntry[];
  currentNodeId: string;
  ended: boolean;
  endReason: string | null;
  /** The outcomes a human tester can pick from right now; empty once ended. */
  choices: string[];
}

function findNode(document: FlowDocument, nodeId: string): FlowNode | undefined {
  return document.nodes.find((node) => node.id === nodeId);
}

function halt(state: SimulationState, reason: string): SimulationState {
  return { ...state, ended: true, endReason: reason, choices: [] };
}

/**
 * Runs from `state.currentNodeId` for as long as it lands on auto-advance
 * types with a wired `"next"` edge, stopping at the first real decision
 * point, a terminal node, or a halt condition (a missing node, a node with
 * no way to continue, or the step cap — a defensive bound against a cyclic,
 * still-being-edited document with no cycle-detection of its own yet on
 * this web side).
 */
function advance(document: FlowDocument, state: SimulationState): SimulationState {
  let current = state;
  for (let step = 0; step < MAX_STEPS; step += 1) {
    const node = findNode(document, current.currentNodeId);
    if (!node) {
      return halt(current, `node "${current.currentNodeId}" does not exist`);
    }
    if (node.type === "hangup") {
      return halt(current, "hangup");
    }
    if (node.type === "transfer") {
      // A successful transfer has no port of its own to check for wiring;
      // only "failure" is a real edge. With none wired, nothing else this
      // node could do — auto-end as a success rather than asking a tester
      // to "choose" an outcome that could never be anything else.
      const failureWired = document.edges.some(
        (candidate) => candidate.source === node.id && candidate.port === "failure",
      );
      if (!failureWired) {
        return halt(current, "transfer succeeded");
      }
      return { ...current, ended: false, endReason: null, choices: ["failure", TRANSFER_SUCCESS] };
    }
    if (!AUTO_ADVANCE_TYPES.has(node.type)) {
      const choices = requiredPorts(node);
      if (choices.length === 0) {
        return halt(current, `"${node.id}" (${node.type}) has no way to continue`);
      }
      return { ...current, ended: false, endReason: null, choices };
    }
    const edge = document.edges.find((candidate) => candidate.source === node.id && candidate.port === AUTO_ADVANCE_PORT);
    if (!edge) {
      return halt(current, `"${node.id}" (${node.type}) has no outgoing edge for port "${AUTO_ADVANCE_PORT}"`);
    }
    const targetType = findNode(document, edge.target)?.type ?? "unknown";
    current = {
      trace: [...current.trace, { nodeId: edge.target, nodeType: targetType, arrivedVia: AUTO_ADVANCE_PORT }],
      currentNodeId: edge.target,
      ended: false,
      endReason: null,
      choices: [],
    };
  }
  return halt(current, "step limit reached (possible unbounded loop)");
}

export function startSimulation(document: FlowDocument): SimulationState {
  const startNode = findNode(document, document.start_node_id);
  return advance(document, {
    trace: [{ nodeId: document.start_node_id, nodeType: startNode?.type ?? "unknown", arrivedVia: null }],
    currentNodeId: document.start_node_id,
    ended: false,
    endReason: null,
    choices: [],
  });
}

/** A stale click (a re-render with a new document, an already-ended run, an
 * outcome that no longer applies) is a silent no-op, not a crash. */
export function chooseSimulationOutcome(document: FlowDocument, state: SimulationState, outcome: string): SimulationState {
  if (state.ended || !state.choices.includes(outcome)) {
    return state;
  }
  const node = findNode(document, state.currentNodeId);
  if (!node) {
    return state;
  }
  if (node.type === "transfer" && outcome === TRANSFER_SUCCESS) {
    return halt(state, "transfer succeeded");
  }
  const edge = document.edges.find((candidate) => candidate.source === node.id && candidate.port === outcome);
  if (!edge) {
    return halt(state, `"${node.id}" (${node.type}) has no outgoing edge for port "${outcome}"`);
  }
  const targetType = findNode(document, edge.target)?.type ?? "unknown";
  return advance(document, {
    trace: [...state.trace, { nodeId: edge.target, nodeType: targetType, arrivedVia: outcome }],
    currentNodeId: edge.target,
    ended: false,
    endReason: null,
    choices: [],
  });
}
